import dayjs from 'dayjs';
import 'dayjs/locale/es';
import { ComparisonRow } from './expenses-summary-comparison-table';

// A summary row flattened to a common shape, so the same helpers work whether
// the grouping axis is the supplier account or the resource category. Both
// axes are fetched one level finer than they are displayed, so every row also
// carries the resource it belongs to — that is what the drill-down reads.
export type NormalizedRow = {
    year: number;
    // 1-indexed, as the backend returns it (dayjs months are 0-indexed).
    month: number;
    total: number;
    entityId: number;
    entityName: string;
    resourceId: number;
    resourceName: string;
};

// A row reduced to whichever level is currently being aggregated. Everything
// below works on this shape, which is why the same helpers serve the parent
// table and its drill-down.
export type SummaryPoint = {
    year: number;
    month: number;
    total: number;
    id: number;
    name: string;
};

export function atEntityLevel(rows: NormalizedRow[]): SummaryPoint[] {
    return rows.map((row) => ({
        year: row.year,
        month: row.month,
        total: row.total,
        id: row.entityId,
        name: row.entityName,
    }));
}

export function atResourceLevel(rows: NormalizedRow[]): SummaryPoint[] {
    return rows.map((row) => ({
        year: row.year,
        month: row.month,
        total: row.total,
        id: row.resourceId,
        name: row.resourceName,
    }));
}

// Build a YYYY-MM-01 dayjs from a 1-indexed month, then step by whole months.
// Going through a first-of-month date avoids the end-of-month overflow trap
// (setting month on the 31st can roll into the following month).
export function shiftMonth(year: number, month: number, offset: number) {
    const date = dayjs(`${year}-${String(month).padStart(2, '0')}-01`).add(
        offset,
        'month',
    );
    return { year: date.year(), month: date.month() + 1 };
}

export function monthLabelOf(year: number, month: number) {
    const label = dayjs(`${year}-${String(month).padStart(2, '0')}-01`)
        .locale('es')
        .format('MMMM YYYY');
    return label.charAt(0).toUpperCase() + label.slice(1);
}

// Totals for one (year, month) bucket, keyed by entity id.
export function bucketByEntity(
    points: SummaryPoint[],
    year: number,
    month: number,
) {
    const map = new Map<number, { name: string; total: number }>();
    points.forEach((point) => {
        if (point.year !== year || point.month !== month) return;
        const existing = map.get(point.id);
        if (existing) {
            existing.total += point.total;
        } else {
            map.set(point.id, { name: point.name, total: point.total });
        }
    });
    return map;
}

export function sumBucket(bucket: Map<number, { name: string; total: number }>) {
    let total = 0;
    bucket.forEach((entry) => {
        total += entry.total;
    });
    return total;
}

// An entity that stopped appearing (missing) or just started appearing
// (first-timer). `amount` is the previous-month total for a missing entity and
// the selected-month total for a first-timer.
export type PresenceRow = {
    id: number;
    name: string;
    amount: number;
    // How many of the six months before the selected one had a captured
    // expense. Separates "appears every single month, definitely missing" (6)
    // from "showed up once, probably a one-off" (1). Only set for missing rows.
    recentMonths?: number;
};

// How many months back `recentMonths` counts over. Exported so the UI can label
// the column with the same number the calculation uses.
export const RECENT_WINDOW = 6;

// Presence means "a row was captured", not "the total is non-zero": with
// "Excluir préstamos" on, a loan-only supplier still returns a row whose total
// is zeroed, and that supplier has not gone missing — it was captured. So every
// check below reads the presence of the bucket entry, never its amount.
function presentIds(points: SummaryPoint[], year: number, month: number) {
    return bucketByEntity(points, year, month);
}

// Entities with a captured expense in the previous month and none in the
// selected one. This is the whole missed-expense heuristic, and it is
// deliberately strict month-vs-previous-month: the scenario being caught is a
// payment due THIS month that was never captured, and a trailing-N window would
// dilute that signal with old history.
export function findMissing(
    points: SummaryPoint[],
    year: number,
    month: number,
): PresenceRow[] {
    const current = presentIds(points, year, month);
    const previousRef = shiftMonth(year, month, -1);
    const previous = presentIds(points, previousRef.year, previousRef.month);

    // How regular each entity has been over the six months preceding the
    // selected one, so a habitual supplier ranks obviously above a one-off.
    const recentCounts = new Map<number, number>();
    for (let offset = 1; offset <= RECENT_WINDOW; offset++) {
        const ref = shiftMonth(year, month, -offset);
        presentIds(points, ref.year, ref.month).forEach((_, id) => {
            recentCounts.set(id, (recentCounts.get(id) ?? 0) + 1);
        });
    }

    const rows: PresenceRow[] = [];
    previous.forEach((entry, id) => {
        if (current.has(id)) return;
        rows.push({
            id,
            name: entry.name,
            amount: entry.total,
            recentMonths: recentCounts.get(id) ?? 0,
        });
    });

    // Regularity outranks size: a supplier that billed all six previous months
    // and vanished is a likelier oversight than a bigger one-off, so sort by
    // how habitual it is first and only then by amount.
    rows.sort(
        (a, b) =>
            (b.recentMonths ?? 0) - (a.recentMonths ?? 0) ||
            b.amount - a.amount,
    );
    return rows;
}

// Entities appearing in the selected month with nothing in the twelve months
// before it. Unlike the missing check, a 12-month window is right here: this is
// a novelty check (typo-duplicate suppliers, misclassified captures), not a
// missing-payment check. The window always falls inside the two fetched years.
export function findFirstTimers(
    points: SummaryPoint[],
    year: number,
    month: number,
): PresenceRow[] {
    const current = presentIds(points, year, month);

    const history = new Set<number>();
    for (let offset = 1; offset <= 12; offset++) {
        const ref = shiftMonth(year, month, -offset);
        presentIds(points, ref.year, ref.month).forEach((_, id) => {
            history.add(id);
        });
    }

    const rows: PresenceRow[] = [];
    current.forEach((entry, id) => {
        if (history.has(id)) return;
        rows.push({ id, name: entry.name, amount: entry.total });
    });

    rows.sort((a, b) => b.amount - a.amount);
    return rows;
}

// Compare every entity present in either the selected month or the previous
// one. Ordering is deliberately left to the table, which defaults to the
// biggest absolute move and lets the user sort by any column instead.
export function buildComparisonRows(
    points: SummaryPoint[],
    year: number,
    month: number,
): ComparisonRow[] {
    const current = bucketByEntity(points, year, month);
    const previousRef = shiftMonth(year, month, -1);
    const previous = bucketByEntity(points, previousRef.year, previousRef.month);

    // The three months immediately before the selected one.
    const trailing = [1, 2, 3]
        .map((offset) => shiftMonth(year, month, -offset))
        .map((ref) => bucketByEntity(points, ref.year, ref.month));

    // Array.from, not a spread: the build targets es5 without
    // downlevelIteration, so spreading a Map iterator does not compile.
    const ids = new Set<number>([
        ...Array.from(current.keys()),
        ...Array.from(previous.keys()),
    ]);

    const comparisonRows: ComparisonRow[] = [];
    ids.forEach((id) => {
        const currentEntry = current.get(id);
        const previousEntry = previous.get(id);
        const currentTotal = currentEntry?.total ?? 0;
        const previousTotal = previousEntry?.total ?? 0;
        const delta = currentTotal - previousTotal;

        // Growing from zero has no finite percentage — null renders as "nuevo".
        const deltaPct = previousTotal
            ? (delta / previousTotal) * 100
            : currentTotal
            ? null
            : 0;

        const avg3 =
            trailing.reduce(
                (acc, bucket) => acc + (bucket.get(id)?.total ?? 0),
                0,
            ) / trailing.length;

        // No trailing history and nothing this month → nothing to say (em dash).
        const vsAvgPct = avg3
            ? ((currentTotal - avg3) / avg3) * 100
            : currentTotal
            ? null
            : undefined;

        comparisonRows.push({
            id,
            name: currentEntry?.name ?? previousEntry?.name ?? '—',
            previous: previousTotal,
            current: currentTotal,
            delta,
            deltaPct,
            avg3,
            vsAvgPct,
        });
    });

    return comparisonRows;
}
